import {
    ActionIcon,
    Button,
    createTheme,
    type CSSVariablesResolver,
    Modal,
    v8CssVariablesResolver,
} from '@mantine/core';
import actionIconClasses from './ActionIcon.module.css';
import buttonClasses from './Button.module.css';
import modalClasses from './Modal.module.css';

export const theme = createTheme({
    /** Put your mantine theme override here */
    fontFamily: '"Inter Variable", sans-serif',
    fontFamilyMonospace: '"Geist Mono Variable", monospace',
    defaultRadius: 'sm',
    headings: {
        fontFamily: '"Inter Variable", sans-serif',
    },
    primaryColor: 'rawmotion',
    colors: {
        rawmotion: [
            '#ffebf1',
            '#fad2de',
            '#f89fbb',
            '#f86b96',
            '#f74377',
            '#f82e64',
            '#f9255a',
            '#de1b4a',
            '#c61241',
            '#b00038',
        ],
        error: [
            '#fff0f2',
            '#ffd7dd',
            '#ffb0bc',
            '#ff8797',
            '#f25a6d', // from figma
            '#ea2424', // from figma
            '#b73646', // from figma
            '#77232c',
            '#381015', // from figma
            '#241212', // from figma
        ],
    },
    /** Global override behaviours */
    components: {
        Button: Button.extend({
            classNames: buttonClasses,
        }),
        ActionIcon: ActionIcon.extend({
            classNames: actionIconClasses,
        }),
        Modal: Modal.extend({ classNames: modalClasses }),
    },
    other: {
        background: {
            primary: {
                dark: '#0D0D0D',
                light: '#fdfbfbff',
            },
            secondary: {
                dark: '#222325ff',
                light: '#f0f0f4ff',
            },
            tertiary: {
                dark: '#262729ff',
                light: '#ddd6d6ff',
            },
        },

        text: {
            primary: {
                dark: '#ffffff',
                light: '#000000',
            },
            secondary: {
                dark: '#e7e3e3ff',
                light: '#211e1fff',
            },
            tertiary: {
                dark: '#e7e3e3ff',
                light: '#211e1fff',
            },
            dimmed: {
                dark: '#6E6E6E',
                light: '#A5A5A5',
            },
            disabled: {
                dark: '#3f3d3eff',
                light: '#211e1fff',
            },
        },

        error: {
            dark: '#f92525ff',
            light: '#f92525ff',
        },

        warning: {
            dark: '#e45e0aff',
            light: '#e45e0aff',
        },

        info: {
            dark: '#ffffff',
            light: '#ffffff',
        },

        connected: {
            dark: '#4caf50',
            light: '#4caf50',
        },

        disconnected: {
            dark: '#ee2b1eff',
            light: '#ee2b1eff',
        },
        eventStatusLive: {
            dark: '#4caf50',
            light: '#4caf50',
        },
        eventStatusPost: {
            dark: '#2196f3',
            light: '#2196f3',
        },
        eventStatusPreparation: {
            dark: '#ff6600ff',
            light: '#ff6600ff',
        },
        eventStatusArchived: {
            dark: '#9e9e9e',
            light: '#9e9e9e',
        },
        scoringStatusNone: {
            dark: 'white',
            light: 'white',
        },
        scoringStatusScoring: {
            dark: '#FFA11E',
            light: '#FFA11E',
        },
        scoringStatusScored: {
            dark: '#E738FF',
            light: '#E738FF',
        },
        scoringStatusLocked: {
            dark: '#FF1F1C',
            light: '#FF1F1C',
        },
        scoringStatusApproved: {
            dark: '#30EAFF',
            light: '#30EAFF',
        },
        scoringStatusReleased: {
            dark: '#0CFF00',
            light: '#0CFF00',
        },
    },
});

export const cssResolver: CSSVariablesResolver = (theme) => {
    const v8Variables = v8CssVariablesResolver(theme);

    return {
        variables: {
            ...v8Variables.variables,
        },
        light: {
            ...v8Variables.light,
            '--mantine-color-body': theme.other.background.primary.light,
            '--mantine-color-body-light': theme.other.background.primary.light,
            '--mantine-color-body2': theme.other.background.secondary.light,
            '--mantine-color-body2-light': theme.other.background.secondary.light,
            '--mantine-color-body3': theme.other.background.tertiary.light,
            '--mantine-color-body3-light': theme.other.background.tertiary.light,
            '--mantine-color-text': theme.other.text.primary.light,
            '--mantine-color-text-light': theme.other.text.primary.light,
            '--mantine-color-error': theme.other.error.light,
            '--mantine-color-warning': theme.other.warning.light,
            '--mantine-color-info': theme.other.info.light,
            '--mantine-color-dimmed': theme.other.text.dimmed.light,
            '--mantine-color-disabled': theme.other.text.disabled.light,
            '--mantine-color-eventstatus-live': theme.other.eventStatusLive.light,
            '--mantine-color-eventstatus-preparation': theme.other.eventStatusPreparation.light,
            '--mantine-color-eventstatus-post': theme.other.eventStatusPost.light,
            '--mantine-color-eventstatus-archived': theme.other.eventStatusArchived.light,
            '--mantine-color-anchor': theme.other.text.primary.light,
            '--mantine-color-scoringStatusNone': theme.other.scoringStatusNone.light,
            '--mantine-color-scoringStatusScoring': theme.other.scoringStatusScoring.light,
            '--mantine-color-scoringStatusScored': theme.other.scoringStatusScored.light,
            '--mantine-color-scoringStatusLocked': theme.other.scoringStatusLocked.light,
            '--mantine-color-scoringStatusApproved': theme.other.scoringStatusApproved.light,
            '--mantine-color-scoringStatusReleased': theme.other.scoringStatusReleased.light,
        },
        dark: {
            ...v8Variables.dark,
            '--mantine-color-body': theme.other.background.primary.dark,
            '--mantine-color-body-dark': theme.other.background.primary.dark,
            '--mantine-color-body2': theme.other.background.secondary.dark,
            '--mantine-color-body2-dark': theme.other.background.secondary.dark,
            '--mantine-color-body3': theme.other.background.tertiary.dark,
            '--mantine-color-body3-dark': theme.other.background.tertiary.dark,
            '--mantine-color-text': theme.other.text.primary.dark,
            '--mantine-color-text-dark': theme.other.text.primary.dark,
            '--mantine-color-error': theme.other.error.dark,
            '--mantine-color-warning': theme.other.warning.dark,
            '--mantine-color-info': theme.other.info.dark,
            '--mantine-color-dimmed': theme.other.text.dimmed.dark,
            '--mantine-color-disabled': theme.other.text.disabled.dark,
            '--mantine-color-eventstatus-live': theme.other.eventStatusLive.dark,
            '--mantine-color-eventstatus-preparation': theme.other.eventStatusPreparation.dark,
            '--mantine-color-eventstatus-post': theme.other.eventStatusPost.dark,
            '--mantine-color-eventstatus-archived': theme.other.eventStatusArchived.dark,
            '--mantine-color-anchor': theme.other.text.primary.dark,
            '--mantine-color-scoringStatusNone': theme.other.scoringStatusNone.dark,
            '--mantine-color-scoringStatusScoring': theme.other.scoringStatusScoring.dark,
            '--mantine-color-scoringStatusScored': theme.other.scoringStatusScored.dark,
            '--mantine-color-scoringStatusLocked': theme.other.scoringStatusLocked.dark,
            '--mantine-color-scoringStatusApproved': theme.other.scoringStatusApproved.dark,
            '--mantine-color-scoringStatusReleased': theme.other.scoringStatusReleased.dark,
        },
    };
};

export type AppTheme = typeof theme;
